import { useState, useCallback } from "react";
import { toast } from "sonner";

async function clipboardWrite(text) {
  try { await navigator.clipboard.writeText(text); } catch {
    const ta = document.createElement("textarea"); ta.value = text;
    document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
  }
}

export function useMultiSectionExport({ sections, sectionNames, editedContent, exportMeta, api, exportToGoogleDocs }) {
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [exporting, setExporting] = useState(false);

  const getMergedContent = useCallback((name) => editedContent[name] || sections[name] || "", [editedContent, sections]);

  const copyAll = useCallback(async () => {
    const allText = sectionNames.map((name) => `### ${name}\n\n${getMergedContent(name)}`).join("\n\n---\n\n");
    await clipboardWrite(allText);
    setCopiedAll(true); setTimeout(() => setCopiedAll(false), 2000);
    toast.success("All sections copied!");
  }, [sectionNames, getMergedContent]);

  const getMarkdownText = useCallback(() =>
    sectionNames.map((name) => `## ${name}\n\n${getMergedContent(name)}`).join("\n\n---\n\n"),
  [sectionNames, getMergedContent]);

  const copyMarkdown = useCallback(async () => {
    await clipboardWrite(getMarkdownText());
    setCopiedMarkdown(true); setTimeout(() => setCopiedMarkdown(false), 2000);
    toast.success("Markdown copied!");
  }, [getMarkdownText]);

  const getHtmlText = useCallback(() => {
    const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return sectionNames.map((name) => {
      return `<h2>${esc(name)}</h2>\n<p>${esc(getMergedContent(name)).replace(/\n/g, "<br>")}</p>`;
    }).join("\n\n<hr>\n\n");
  }, [sectionNames, getMergedContent]);

  const copyHtml = useCallback(async () => {
    await clipboardWrite(getHtmlText());
    setCopiedHtml(true); setTimeout(() => setCopiedHtml(false), 2000);
    toast.success("HTML copied!");
  }, [getHtmlText]);

  const openInGoogleDocs = useCallback(async () => {
    const allSections = {};
    sectionNames.forEach((name) => { allSections[name] = getMergedContent(name); });
    await exportToGoogleDocs({
      title: exportMeta?.title || exportMeta?.topic || "Quibn Export",
      sections: allSections,
      section_names: sectionNames,
    });
  }, [sectionNames, getMergedContent, exportMeta, exportToGoogleDocs]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const resp = await api.post("/export/pdf", {
        title: exportMeta?.title || exportMeta?.topic || "Multi-Section Content",
        content_type: exportMeta?.content_type || "",
        platform: exportMeta?.platform || "",
        audience: exportMeta?.audience || "",
        ai_model: exportMeta?.ai_model || "",
        draft: "",
        sections: Object.fromEntries(sectionNames.map((n) => [n, getMergedContent(n)])),
        section_names: sectionNames,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `${(exportMeta?.title || "content").substring(0, 50).replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  }, [api, exportMeta, sectionNames, getMergedContent]);

  return {
    copiedAll, copyAll, copiedMarkdown, copyMarkdown, copiedHtml, copyHtml,
    exporting, handleExportPdf, openInGoogleDocs,
  };
}
